import { NgClass } from "@angular/common";
import { AfterViewInit, Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { AddFriendResponse, UserSearchContext, UserSearchResponse, UserSearchResult } from "@apiserver-rest/ponder-data-client-base";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { debounceTime, distinctUntilChanged, fromEvent, map, Subscription, take } from "rxjs";
import { UserFriendAddRowComponent } from "./user-friend-add-row.component";
import { EventBroker } from "@ponder-core/events/eventBroker";

@Component({
    selector: "user-friend-add",
    imports: [LoadingComponent, UserFriendAddRowComponent, NgClass],
    templateUrl: "user-friend-add.component.html"
})
export class UserFriendAddComponent implements AfterViewInit {
    @ViewChild("addFriendModal") private addFriendModalRef!: ElementRef<HTMLDivElement>;
    @ViewChild("userSearch") private userSearchRef!: ElementRef<HTMLInputElement>;

    @Input({ required: true }) public username!: string;

    public isVisible: boolean = false;
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;

    private searchSub!: Subscription;
    private addFriendSub!: Subscription;

    constructor(private ponderDataClient: PonderDataClient, private events: EventBroker) {
        this.addFriendSub = this.events.profile.addFriendEvent.subscribe({
            next: (friendUserId: string) => {
                this.onAddFriend(friendUserId);
            }
        });
    }

    public ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
        this.addFriendSub?.unsubscribe();
    }

    public ngOnInit(): void {

    }

    public ngAfterViewInit(): void {
        this.searchSub = fromEvent(this.userSearchRef.nativeElement, "input")
            .pipe(map((event: Event) => (event.target as HTMLInputElement).value))
            .pipe(debounceTime(750))        // keep same as primary search debounce for consistency
            .pipe(distinctUntilChanged())
            .subscribe({
                next: (value: string) => {
                    this.onInputChange(value);
                }
            });
    }

    public onAddClick(): void {
        this.onOpen();
    }

    public onOpen(): void {
        this.isVisible = true;
    }

    public onClose(): void {
        this.isVisible = false;
        this.isSearching = false;
        this.hasSearchedOnce = false;
        this.searchError = false;
        this.searchResults = undefined;
        this.isAdding = false;
        this.addError = false;
        
        if (this.userSearchRef) {
            this.userSearchRef.nativeElement.value = "";
        }
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.isVisible && this.addFriendModalRef && !this.addFriendModalRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.isVisible && this.addFriendModalRef && this.mouseDownOutside && !this.addFriendModalRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // Search
    // -------------------------------------------------------------------------------------

    public isSearching: boolean = false;
    public hasSearchedOnce: boolean = false;
    public searchError: boolean = false;
    public searchResults?: UserSearchResult[];

    private onInputChange(value: string): void {
        if (this.isSearching) {
            // todo probably need to cancel current search and start a new one
            // we have already restricted time between searches with the debounce on the event
            // this is just hiding real searches
            // todo also the same to the main and deck card search
            return;
        }
        
        this.searchResults = undefined;

        if (value.length < 3) {
            return;
        }

        this.isSearching = true;
        this.searchError = false;
        this.hasSearchedOnce = true;

        this.ponderDataClient
            .searchUser(value, UserSearchContext.AddFriend)
            .pipe(take(1))
            .subscribe({
                next: (response: UserSearchResponse) => {
                    if (response.success) {
                        this.searchResults = response.results;
                        this.sortAndFilterResults(value);
                    } else {
                        this.searchError = true;
                    }
                },
                error: () => {
                    this.searchError = true;
                }
            })
            .add(() => { this.isSearching = false; });
    }

    private sortAndFilterResults(searchTerm: string): void {
        if (!this.searchResults) {
            return;
        }

        // First, remove ourselves if needed.
        var lowerUsername = this.username.toLocaleLowerCase();
        var selfIndex = -1;

        for (let i = 0; i < this.searchResults.length; ++i) {
            if (this.searchResults[i].username!.toLocaleLowerCase() == this.username) {
                selfIndex = i;
                break;
            }
        }

        if (selfIndex >= 0) {
            this.searchResults = this.searchResults.splice(selfIndex, 1);
        }

        // Now sort
        var lowerSearchTerm = searchTerm.toLocaleLowerCase();

        this.searchResults.sort((a, b) => {
            var aUsernameLower = a.username!.toLocaleLowerCase();
            var aDisplayNameLower = a.displayName?.toLocaleLowerCase();

            var bUsernameLower = b.username!.toLocaleLowerCase();
            var bDisplayNameLower = b.displayName?.toLocaleLowerCase();

            if ((aUsernameLower == lowerSearchTerm) || (aDisplayNameLower == lowerSearchTerm)) {
                return -1;
            }

            if ((bUsernameLower == lowerSearchTerm) || (bDisplayNameLower == lowerSearchTerm)) {
                return 1;
            }

            return aUsernameLower.localeCompare(bUsernameLower);
        });
    }
    
    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    public isAdding: boolean = false;
    public addError: boolean = false;

    private onAddFriend(friendUserId: string): void {
        if (this.isAdding) {
            return;
        }

        this.isAdding = true;

        this.ponderDataClient
            .userFriendAdd(friendUserId)
            .pipe(take(1))
            .subscribe({
                next: (response: AddFriendResponse) => {
                    if (response.success && response.friend) {
                        this.events.profile.friendInviteSentEvent.emit(response.friend);
                    } else {
                        this.addError = true;
                    }
                },
                error: () => {
                    this.addError = true;
                }
            })
            .add(() => this.onClose());
    }
}